import { mapActions } from 'vuex';
import { urlRegexp } from 'Root/helpers/regexps';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import altcraftAnalytics from 'Root/drivers/altcraftAnalytics';
import PaymentSystemModel from 'Models/PaymentSystemModel';
import UserModel from 'Models/UserModel';
import { POPUP_PROMOCODE_APPLIED } from 'Models/PopupModel';
import { getAdvertisingParamsFromSession } from 'Root/helpers/handleAdvertisingParams';
import AppModel from 'Models/AppModel';
import { addBuyLogs } from 'Root/helpers/tools';

const DEFAULT_ERROR_MESSAGE = 'An error has occurred. Try again.';

export default {
  methods: {
    ...mapActions(['getUser']),

    enableInterface() {
      this.loading = false;
      this.$store.dispatch('hideScreenHolder');
    },

    disableInterface() {
      this.loading = true;
      this.$store.dispatch('showScreenHolder', 'payment');
    },

    async order(email, paymentMethodId, eWallet, shouldRedirect) {
      if (this.loading) {
        return null;
      }

      this.error = '';
      this.disableInterface();

      try {
        return await this.createOrder(paymentMethodId, eWallet, shouldRedirect, email);
      } catch (e) {
        this.enableInterface();
        googleTagAnalytics.emitBuyItem(false);
        this.error = DEFAULT_ERROR_MESSAGE;
        console.warn(e.message);
        return null;
      }
    },

    createOrderData(paymentMethodId, eWallet, email) {
      const orderData = {
        runs: this.runsCount,
        payment_system: paymentMethodId,
        region_id: this.activeRegion.id,
        promocode: this.appliedPromocode?.code || null,
        ...(this.$route.query.upsale_ref ? { upsale_ref: this.$route.query.upsale_ref } : {}),
      };

      // Add product-specific data
      if (this.offerData) {
        orderData.product_currency_amount = 1;
        orderData.product_currency_offer_id = this.offerData.id;
      } else if (this.product) {
        orderData.product_id = this.product.id;
      }

      if (!this.user) {
        orderData.email = email;
      }

      const meta = {};

      meta.connectionInfo = this.$store.state.connectionInfo;
      meta.advertisingParams = getAdvertisingParamsFromSession();

      const ewalletMethods = [
        PaymentSystemModel.CARDPAY_2_WEBMONEY.id,
        PaymentSystemModel.CARDPAY_2_QIWI.id,
      ];

      if (ewalletMethods.includes(paymentMethodId)) {
        meta.ewallet_account = eWallet;
      }

      if (this.usedCashback?.isApplied && this.usedCashback?.value) {
        meta.cashback = this.usedCashback.value;
      }

      orderData.meta = JSON.stringify(meta);

      orderData.is_guest = this.isGuest;

      const referee = localStorage.getItem('referee');
      if (referee) {
        orderData.referee = referee;
      }

      const initialURLpage = sessionStorage.getItem('initialURLPage');
      if (initialURLpage) {
        orderData.login_link = initialURLpage;
      }

      return orderData;
    },

    sendAltcraftAnalytics(hasPassword) {
      altcraftAnalytics.firePaymentPopup(this.price.newPrice, this.user, this.userEmail, !hasPassword);
    },

    async createOrder(paymentMethodId, eWallet, shouldRedirect = true, email) {
      const orderData = this.createOrderData(paymentMethodId, eWallet, email);

      let data;
      try {
        const { API_URI } = AppModel;

        const response = await this.$axios
          .post(`${API_URI}/product/${this.product?.id || this.offerData?.id}/buy`,
            orderData, this._getFingerprintHeader());
        data = response.data;

        if (data.skycoach_token) {
          await this.getUser();
          this.sendAltcraftAnalytics(this?.user?.has_password);
          localStorage.setItem(UserModel.SET_PASSWORD_TOKEN_NAME, data.skycoach_token);
        } else {
          this.sendAltcraftAnalytics(this?.user?.has_password);
        }
      } catch (error) {
        this.enableInterface();

        if (error?.response?.data?.message) {
          addBuyLogs(error);
          this.orderError = error?.response?.data?.message;
        }

        if (this.orderError === 'Promocode usage exceeded') {
          this.appliedPromocode = null;
        }
        return null;
      }

      this.$cookie.delete(POPUP_PROMOCODE_APPLIED.cookieName);
      googleTagAnalytics.emitBuyItem(true);

      if (!data?.url) {
        return null;
      }

      if ('localStorage' in window) {
        localStorage.setItem('activeGameId', this.$store.state.activeGame.id);
      }

      if (shouldRedirect) {
        if (data.url.match(urlRegexp)) {
          const pathToRedirect = new URL(data.url);
          const emailParam = pathToRedirect.searchParams.get('email');
          if (!this.user && !emailParam && paymentMethodId !== PaymentSystemModel.ECOMMPAY.id) {
            pathToRedirect.searchParams.append('email', email);
          } else if (paymentMethodId === PaymentSystemModel.ECOMMPAY.id) {
            pathToRedirect.searchParams.delete('email');
          }

          document.location.href = pathToRedirect.href;
        } else if (window.stripe) {
          try {
            const { error } = await window.stripe.redirectToCheckout({
              sessionId: data.url,
            });

            if (error) {
              this.enableInterface();
              throw new Error(`Payment Error: ${error.message}`);
            }
          } catch (error) {
            this.enableInterface();
            console.error('Stripe redirect error:', error);
            return null;
          }
        } else {
          this.enableInterface();
          console.error('window.stripe is undefined');
          return null;
        }
      }

      return data;
    },
  },
};
